import React from 'react';
import PropTypes from 'prop-types';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import UIImage from './ui/UIImage';
import { Select } from '../../ArtistComponents/Form';
import useComponent from '../../../../hooks/useComponent';
import { setValue } from '../../../../store/actions';
import TIMEZONES from '../../../../utils/timezones';
import { transformDataToSelectOptions } from '../../../../utils/selectHandlers';

const List = ({ id, data, link, activeLanguage, image, title, handleAddRow, handleUpdateRow, handleDeleteRow, handleScheduleImage, handleRemoveSchedule, handleScheduleTitle, handleScheduleLink }) => (
  <div className="flex-1 p-4 border border-indigo-lightest rounded-bl-lg relative mb-8 text-black">
    <div className="w-ful bg-indigo-lightest p-4 rounded-tr-lg rounded-tl-lg flex items-center justify-between">
      <h3 className="text-lg font-bold">Table {id}</h3>
      <button onClick={() => handleRemoveSchedule(id)} type="button"> Remove Schedule </button>
    </div>
    <UIImage label="Image" activeLanguage={activeLanguage} getImageUrl={handleScheduleImage(activeLanguage, id)} imageUrl={image} />
    <label htmlFor="title" className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
      Link
      <input
        type="text"
        value={link[activeLanguage] || ''}
        name="link"
        onChange={e => handleScheduleLink(activeLanguage, e, id)}
        className="appearance-none block w-full bg-gray-200 text-gray-700 border border-red-500 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
      />
    </label>
    <label htmlFor="title" className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
      title
      <input
        type="text"
        value={title[activeLanguage] || ''}
        name="title"
        onChange={e => handleScheduleTitle(activeLanguage, e, id)}
        className="appearance-none block w-full bg-gray-200 text-gray-700 border border-red-500 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
      />
    </label>
    <label htmlFor="data" className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
      Schedule
      <table className="table-auto w-full">
        <thead>
          <tr>
            <th className="px-4 py-2">Artist</th>
            <th className="px-4 py-2">Day (YYYY-MM-DD)</th>
            <th className="px-4 py-2">Time (HH:MM)</th>
            <th className="px-4 py-2">Venue</th>
            <th className="px-4 py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {data.map((rowData, rowIndex) => {
            const { artist, day, dateTime, venue } = rowData;
            return (
              <tr key={rowIndex}>
                <td className="border px-4 py-2">
                  <input
                    type="text"
                    value={artist}
                    name="artist"
                    onChange={e => handleUpdateRow(id, rowIndex, e)}
                    className="w-full focus:outline-none"
                  />
                </td>
                <td className="border px-4 py-2">
                  <input
                    type="text"
                    value={day}
                    name="day"
                    onChange={e => handleUpdateRow(id, rowIndex, e)}
                    className="w-full focus:outline-none"
                  />
                </td>
                <td className="border px-4 py-2">
                  <input
                    type="text"
                    value={dateTime}
                    name="dateTime"
                    onChange={e => handleUpdateRow(id, rowIndex, e)}
                    className="w-full focus:outline-none"
                  />
                </td>
                <td className="border px-4 py-2">
                  <input
                    type="text"
                    value={venue}
                    name="venue"
                    onChange={e => handleUpdateRow(id, rowIndex, e)}
                    className="w-full focus:outline-none"
                  />
                </td>
                <td className="border px-4 py-2">
                  <button
                    type="button"
                    onClick={() => handleDeleteRow(id, rowIndex)}
                    className="text-red-500 hover:text-red-700 focus:outline-none"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </label>
    <button type="button" className="mt-4 hover:border-indigo-darker cursor-pointer border border-indigo rounded-lg p-2 text-black mb-1" onClick={() => handleAddRow(id)}>
      Add Row
    </button>
  </div>
);

const scheduleData = {
  artist: '',
  day: '',
  dateTime: '',
  venue: '',
};


const scheduleInitialState = {
  image: {
    en: '',
    es: '',
    ca: '',
    pt: '',
  },
  link: {
    en: '',
    es: '',
    ca: '',
    pt: '',
  },
  title: {
    en: '',
    es: '',
    ca: '',
    pt: '',
  },
  data: [
    scheduleData,
  ],
};

const initialState = {
  data: {
    type: 'SimpleScheduleTable',
    schedules: [],
    timezone: 'Europe/Madrid',
  },
  error: false,
};

const SimpleScheduleTable = ({ handlerSetComponents, components, id, reusableComponent, activeLanguage, }) => {
  const { state: { data }, dispatch } = useComponent({ initialState, setComponents: handlerSetComponents, components, id, reusableComponent });

  const { schedules = [] } = data || {};

  const setSchedules = updatedSchedules => setValue(dispatch)('schedules', updatedSchedules);

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const updatedTables = Array.from(schedules);
    const [movedTable] = updatedTables.splice(result.source.index, 1);
    updatedTables.splice(result.destination.index, 0, movedTable);
    setSchedules(updatedTables);
  };

  const handleAddRow = (tableId) => {
    setSchedules([...schedules, { id: `schedule${schedules.length + 1}`, ...scheduleInitialState }]);
    const updatedTables = schedules.map((table) => {
      if (table.id === tableId) {
        const updatedData = [...table.data];
        updatedData.push(scheduleData);
        return {
          ...table,
          data: updatedData,
        };
      }
      return table;
    });
    setSchedules(updatedTables);
  };

  const handleDeleteRow = (tableId, rowIndex) => {
    const updatedTables = schedules.map((table) => {
      if (table.id === tableId) {
        const updatedData = [...table.data];
        updatedData.splice(rowIndex, 1);
        return {
          ...table,
          data: updatedData,
        };
      }
      return table;
    });
    setSchedules(updatedTables);
  };

  const handleUpdateRow = (tableId, rowIndex, event) => {
    const { value, name } = event.target;
    const updatedTables = schedules.map((table) => {
      if (table.id === tableId) {
        const updatedData = [...table.data];
        updatedData[rowIndex] = {
          ...updatedData[rowIndex],
          [name]: value,
        };
        return {
          ...table,
          data: updatedData,
        };
      }
      return table;
    });
    setSchedules(updatedTables);
  };

  const handleScheduleImage = (language, tableId) => (imageUrl) => {
    const updatedTables = schedules.map((table) => {
      if (table.id === tableId) {
        const updatedData = [...table];
        updatedData.image[language] = imageUrl;
      }
      return table;
    });
    setSchedules(updatedTables);
  };

  const handleAddSchedule = () => {
    setSchedules([...schedules, { id: `table${schedules.length + 1}`, ...scheduleInitialState }]);
  };

  const handleRemoveSchedule = (tableId) => {
    const scheduleIndex = schedules.findIndex(table => table.id === tableId);
    if (scheduleIndex === -1) return;
    const updatedTables = [...schedules];
    updatedTables.splice(scheduleIndex, 1);
    setSchedules(updatedTables);
  };

  const handleScheduleTitle = (language, e, tableId) => {
    const scheduleIndex = schedules.findIndex(table => table.id === tableId);
    if (scheduleIndex === -1) return;
    const { name, value } = e.target;
    const updatedTables = [...schedules];
    const currentTable = { ...updatedTables[scheduleIndex] };
    const updateCurrentTable = {
      ...currentTable,
      [name]: {
        ...currentTable[name],
        [language]: value,
      },
    };
    updatedTables[scheduleIndex] = updateCurrentTable;
    setSchedules(updatedTables);
  };

  const handleScheduleLink = (language, e, tableId) => {
    const scheduleIndex = schedules.findIndex(table => table.id === tableId);
    if (scheduleIndex === -1) return;
    const { name, value } = e.target;
    const updatedTables = [...schedules];
    const currentTable = updatedTables[scheduleIndex];
    const updateCurrentTable = {
      ...currentTable,
      [name]: {
        ...currentTable[name],
        [language]: value,
      },
    };
    updatedTables[scheduleIndex] = updateCurrentTable;
    setSchedules(updatedTables);
  };

  const handleSelect = (name, value) => {
    setValue(dispatch)(name, value.value);
  };

  return (
    <div className="w-full rounded-lg border-indigo border mb-4">
      <div className="w-full bg-indigo p-4 rounded-tr-lg rounded-tl-lg flex items-center justify-between">
        <div className="w-1/2">
          <Select
            name="timezone"
            label="Timezone"
            value={transformDataToSelectOptions(data?.timezone)}
            options={TIMEZONES}
            onChange={value => handleSelect('timezone', value)}
          />
        </div>
        <button className="text-white flex-end" onClick={handleAddSchedule} type="button"> Add new Schedule </button>
      </div>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="scheduleTables">
          {provided => (
            <div ref={provided.innerRef} {...provided.droppableProps} className="p-4">
              {schedules.map(({ id, image, title, link, data }, index) => (
                <Draggable key={id} draggableId={id} index={index}>
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                      <List id={id} image={image} title={title} link={link} data={data} handleAddRow={handleAddRow} handleDeleteRow={handleDeleteRow} handleUpdateRow={handleUpdateRow} handleScheduleImage={handleScheduleImage} activeLanguage={activeLanguage} handleRemoveSchedule={handleRemoveSchedule} handleScheduleTitle={handleScheduleTitle} handleScheduleLink={handleScheduleLink} />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

export default SimpleScheduleTable;
